import React, { useEffect, useState } from "react";
import "./PatientDetails.css";
import { useTranslation } from "react-i18next";
import Icons from "../../../../Assets/Icons";
import { format } from "date-fns";
import NotesSection from "./NotesSection";
import { getSecurePreviewUrl, openSecureFileUrl } from "../../../../utils/secureFilePreview";

const AttachmentImagePreview = ({ attachment }) => {
  const [src, setSrc] = useState(attachment.url);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setShowFallback(false);
    getSecurePreviewUrl(attachment.url)
      .then((previewUrl) => {
        if (!cancelled) setSrc(previewUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setSrc(attachment.url);
          setShowFallback(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attachment.url]);

  useEffect(() => {
    return () => {
      if (src?.startsWith("blob:")) URL.revokeObjectURL(src);
    };
  }, [src]);

  return (
    <>
      {!showFallback && <img src={src} className="attachment-image-fit" alt={attachment.name} onError={() => setShowFallback(true)} />}
      {showFallback && (
        <div className="attachment-preview-fallback" style={{ display: "flex" }}>
          Preview unavailable
        </div>
      )}
    </>
  );
};

const PatientDetails = ({ setPage, setEnabledSteps, data, onVerify }) => {
  const { t } = useTranslation();
  const rawDate = data?.dateOfBirth || null;
  const [patientCondition, setPatientCondition] = useState(data?.patientCondition || "");
  const [patientAddress, setPatientAddress] = useState(data?.address || "");
  const [description, setDescription] = useState(data?.notes || "");
  const [zone, setZone] = useState(data?.distanceInKm ?? "");

  const attachments = data?.attachments || [];

  useEffect(() => {
    setPatientCondition(data?.patientCondition || "");
    setPatientAddress(data?.address || "");
    setDescription(data?.notes || "");
    setZone(data?.distanceInKm ?? "");
  }, [data?.patientCondition, data?.address, data?.notes, data?.distanceInKm]);

  const getFormattedDate = (date) => {
    if (!date) return "-";
    return format(new Date(date), "dd MMM, yyyy");
  };

  const handleAssign = () => {
    if (zone === "" || zone === null || zone === undefined) return;
    onVerify?.({
      patientCondition: patientCondition.trim(),
      address: patientAddress.trim(),
      description: description.trim(),
      zone,
    });
  };

  const isAssignEnabled = zone !== "" && zone !== null && zone !== undefined;

  return (
    <div className="verification-request">
      {/* ── Patient Info Card ── */}
      <div className="pdl-info verify">
        <p className="pdl-title">{t("personalInformation")}</p>
        <div className="pdl-body">
          {/* Left: photo + HN Number */}
          <div className="pdl-section-left">
            <img src={data?.profilePhoto || Icons.noProfile} alt="profile" />
            <div className="pdl-hn pd-hn-center">
              <p className="pdl-hn-title">
                {t("hnNumber")}
                <span className="asterik">*</span>
              </p>
              <p className="pdl-number">{data?.hnNumber || "-"}</p>
            </div>
          </div>

          {/* Right: name → gender+dob → phone+email → insurance → condition+distance */}
          <div className="pdl-section-right">
            <p className="pdl-name">{data?.name}</p>

            <section className="pdl-phone-dob nc-sd-patient">
              <div className="pdl-detail">
                <img src={Icons.gender} alt="gender" />
                <span>{data?.gender ? t(data.gender) : "-"}</span>
              </div>
              <div className="pdl-detail">
                <img src={Icons.CalenderIcon} alt="dob" />
                <span>{getFormattedDate(rawDate)}</span>
              </div>
            </section>

            <section className="pdl-phone-dob nc-sd-patient">
              <div className="pdl-detail">
                <img src={Icons.Call} alt="phone" />
                <span>{data?.phone}</span>
              </div>
              <div className="pdl-detail">
                <img src={Icons.SmsIcon} alt="email" />
                <span>{data?.email || "-"}</span>
              </div>
            </section>

            <div className="sd-hn pd-insurance-row">
              <img src={Icons.insurance} alt="insurance" />
              <span>{data?.insuranceType ? t(data.insuranceType) : "-"}</span>
            </div>

            {/* Patient Condition + Distance by KM */}
            <section className="pdl-phone-dob nc-sd-dropdown pd-condition-zone-row">
              <div className="pdl-hn pd-field-half">
                <p className="pdl-hn-title">{t("patientCondition")}</p>
                <input
                  type="text"
                  className="form-section-input-2"
                  value={patientCondition}
                  onChange={(e) => setPatientCondition(e.target.value)}
                  placeholder={t("patientCondition")}
                />
              </div>
              <div className="pdl-hn pd-field-half">
                <p className="pdl-hn-title">
                  {t("zone")}
                  <span className="asterik">*</span>
                </p>
                <input type="number" className="form-section-input-2" value={zone || ""} onChange={(e) => setZone(e.target.value)} placeholder={t("noofkm")} min="0" />
              </div>
            </section>

            {/* Patient Address */}
            <div className="pdl-hn pd-address-block">
              <p className="pdl-hn-title">{t("patientAddress")}</p>
              <div className="pdl-detail pd-address-text pd-address-input-wrap">
                <img src={Icons.Location} alt="location" />
                <textarea
                  className="form-section-input-2 pd-address-input"
                  value={patientAddress}
                  onChange={(e) => setPatientAddress(e.target.value)}
                  placeholder={t("patientAddress")}
                  rows={2}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attachments — read-only view (no add/remove) */}
      {attachments.length > 0 && (
        <div className="whp-container attachments-section">
          <div className="title-hr2">
            <p>{t("attachments")}</p>
            <hr />
          </div>
          <div className="attachments-grid">
            {attachments.map((att, i) => (
              <div key={i} className="attachment-item">
                <div className="attachment-preview" onClick={() => openSecureFileUrl(att.url)}>
                  {att.type !== "image" ? <img src={Icons.PdfRedIcon} className="pdf-icon-small" alt="PDF" /> : <AttachmentImagePreview attachment={att} />}
                </div>
                <span className="attachment-name">{att.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <NotesSection notes={description} onChange={setDescription} />

      <div className="patient-details-footer">
        <button className="verify-btn" onClick={handleAssign} disabled={!isAssignEnabled}>
          {t("assign") || "Assign"}
        </button>
      </div>
    </div>
  );
};

export default PatientDetails;
